/**
 * InviteFriendsWidget Component
 *
 * Kompaktes "Freunde einladen" Widget im Trade Republic Style.
 * Zeigt dauerhaft auf dem Dashboard/Workspace an.
 * Klick führt zur vollständigen Referral-Seite.
 */
import { Link } from 'react-router-dom';
import { Gift, ChevronRight, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import { useReferralLinks } from './hooks/useReferralLinks';

export function InviteFriendsWidget() {
  const { data: links } = useReferralLinks();
  const [copied, setCopied] = useState(false);

  // Ersten Link finden
  const primaryLink = links?.[0];

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!primaryLink?.url) return;

    try {
      await navigator.clipboard.writeText(primaryLink.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  return (
    <Link
      to="/v2/referral"
      className="block group"
      aria-label="Freunde einladen - zur Referral-Seite"
    >
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 p-4 text-white shadow-lg transition-all hover:shadow-xl hover:scale-[1.02]">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white" />
          <div className="absolute -bottom-2 -left-2 h-16 w-16 rounded-full bg-white" />
        </div>

        {/* Content */}
        <div className="relative flex items-center gap-3">
          {/* Icon */}
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20">
            <Gift className="h-5 w-5" />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm">Freunde einladen</h3>
            <p className="text-xs text-white/80 truncate">
              {primaryLink ? 'Teile deinen Link & verdiene' : 'Erstelle deinen Einladungslink'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            {primaryLink && (
              <button
                onClick={handleCopy}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                aria-label={copied ? 'Link kopiert' : 'Link kopieren'}
              >
                {copied ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </button>
            )}
            <ChevronRight className="h-5 w-5 opacity-60 group-hover:opacity-100 transition-opacity" />
          </div>
        </div>

        {/* Stats (wenn vorhanden) */}
        {primaryLink && (primaryLink.clicks > 0 || primaryLink.conversions > 0) && (
          <div className="relative mt-3 flex gap-4 text-xs text-white/70">
            <span>{primaryLink.clicks} Klicks</span>
            <span>{primaryLink.conversions} Anmeldungen</span>
          </div>
        )}
      </div>
    </Link>
  );
}
